import React, { useState, useMemo } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListSubheader,
    Typography,
    Box,
    Chip,
} from '@mui/material';
import { SearchInput } from '../SearchInput';

const sdpServiceCodes = [
    // Living Arrangement (310-330)
    { code: 310, definition: "Respite Services (Individual and Agency) In-home" },
    { code: 311, definition: "Respite Facility - Out-of-Home" },
    { code: 312, definition: "Live-In Caregiver" },
    { code: 313, definition: "Homemaker" },
    { code: 314, definition: "Housing Access Supports" },
    { code: 315, definition: "FMS Fiscal Agent" },
    { code: 316, definition: "FMS Co-Employer" },
    { code: 317, definition: "FMS Fiscal/Employer Agent" },
    { code: 320, definition: "Community Living Supports (Individual and Agency)" },
    { code: 321, definition: "Residential Facility" },
    // Employment & Community Participation (331-355)
    { code: 331, definition: "Community Integration Supports" },
    { code: 333, definition: "Participant-Directed Goods and Services" },
    { code: 334, definition: "Individual Training and Education" },
    { code: 335, definition: "Employment Supports" },
    { code: 336, definition: "Technology Supports" },
    { code: 337, definition: "Transition Set-Up Expenses" },
    { code: 338, definition: "Non-Medical Transportation" },
    { code: 339, definition: "Prevocational Supports" },
    { code: 340, definition: "Independent Facilitator" },
    // Health and Safety (356-399)
    { code: 356, definition: "Environmental Accessibility" },
    { code: 357, definition: "Acupuncture Services" },
    { code: 358, definition: "Personal Emergency Response Systems (PERS)" },
    { code: 359, definition: "Home Health Aide" },
    { code: 360, definition: "Communication Support" },
    { code: 361, definition: "Skilled Nursing" },
    { code: 362, definition: "Nutritional Consultation" },
    { code: 363, definition: "Crisis Intervention and Supports" },
    { code: 364, definition: "Behavioral Intervention Services" },
    { code: 365, definition: "Specialized Medical Equipment and Supplies" },
    { code: 366, definition: "Family/Consumer Training" },
    { code: 367, definition: "Dental Services" },
    { code: 368, definition: "Lenses and Frames" },
    { code: 369, definition: "Optometric-Optician Services" },
    { code: 370, definition: "Psychology Services" },
    { code: 371, definition: "Training and Counseling Services for Unpaid Caregivers" },
    { code: 372, definition: "Speech - Hearing and Language" },
    { code: 373, definition: "Chiropractor" },
    { code: 374, definition: "Massage Therapist" },
    { code: 375, definition: "Occupational Therapy" },
    { code: 376, definition: "Physical Therapy" },
    { code: 377, definition: "Vehicle Modifications and Adaptations" },
    { code: 378, definition: "Child Day Care Facility" }
];

const SDPServiceCodesDropdownArray = ({
    value,
    onChange,
    label = 'Service Codes',
    disabled = false,
    sx = {},
    onOpen = () => { },
    message = 'Select Service Codes',
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Normalize value to array of numbers
    const normalizedValue = useMemo(() => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value.map((v) => Number(v)).filter((v) => !isNaN(v));
        }
        // Handle single value (for backward compatibility if needed)
        const numValue = Number(value);
        return isNaN(numValue) ? [] : [numValue];
    }, [value]);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return sdpServiceCodes;

        const searchLower = searchTerm.toLowerCase();
        return sdpServiceCodes.filter((service) =>
            String(service.code).toLowerCase().includes(searchLower) ||
            service.definition.toLowerCase().includes(searchLower)
        );
    }, [searchTerm]);

    const handleChange = (event) => {
        const selectedValue = event.target.value;
        // Ensure all values are numbers
        const normalized = Array.isArray(selectedValue)
            ? selectedValue.map((v) => Number(v)).filter((v) => !isNaN(v))
            : [Number(selectedValue)].filter((v) => !isNaN(v));

        if (onChange && typeof onChange === 'function') {
            onChange(normalized);
        }
    };

    const handleMenuItemClick = (optionCode, event) => {
        const optionValue = Number(optionCode);

        // Toggle selection: if already selected, remove it; otherwise add it
        const newSelection = normalizedValue.includes(optionValue)
            ? normalizedValue.filter((v) => v !== optionValue)
            : [...normalizedValue, optionValue];

        if (onChange && typeof onChange === 'function') {
            onChange(newSelection);
        }
    };

    const handleOpen = () => {
        setSearchTerm('');
        onOpen();
    };

    const handleClose = () => {
        setSearchTerm('');
    };

    const getSelectedOptions = () => {
        if (!normalizedValue || normalizedValue.length === 0) return [];
        return normalizedValue
            .map((v) => sdpServiceCodes.find((opt) => opt.code === v))
            .filter(Boolean);
    };

    const selectedOptions = getSelectedOptions();
    const isEmpty = !normalizedValue || normalizedValue.length === 0;

    return (
        <FormControl fullWidth sx={{ ...sx }}>
            <InputLabel
                id='sdp-service-codes-dropdown-array-label'
                shrink={true}
            >
                {label}
            </InputLabel>
            <Select
                labelId='sdp-service-codes-dropdown-array-label'
                id='sdp-service-codes-dropdown-array'
                value={normalizedValue}
                label={label}
                onChange={handleChange}
                disabled={disabled}
                multiple={true}
                displayEmpty
                notched={true}
                renderValue={(selected) => {
                    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                        return (
                            <Typography sx={{ color: 'text.secondary' }}>
                                {message}
                            </Typography>
                        );
                    }

                    if (Array.isArray(selectedOptions) && selectedOptions.length > 0) {
                        return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedOptions.map((option) => (
                                    <Chip
                                        key={option.code}
                                        label={`${option.code} - ${option.definition}`}
                                        size='small'
                                        sx={{
                                            backgroundColor: '#e9eaff',
                                            color: '#9291f0',
                                            fontWeight: 600,
                                        }}
                                    />
                                ))}
                            </Box>
                        );
                    }

                    return (
                        <Typography sx={{ color: 'text.secondary' }}>{message}</Typography>
                    );
                }}
                MenuProps={{
                    PaperProps: {
                        style: { maxHeight: 400, zIndex: 1600 },
                    },
                    autoFocus: false,
                    disablePortal: true,
                    disableScrollLock: true,
                }}
                onOpen={handleOpen}
                onClose={handleClose}
                sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--clr-theme-purple)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--clr-theme-purple)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--clr-theme-purple)',
                    },
                }}
            >
                {/* Search bar inside dropdown */}
                <ListSubheader
                    disableSticky
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        message='Search service codes...'
                    />
                </ListSubheader>

                {/* Render filtered options */}
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((service) => {
                        const optionValue = service.code;
                        const isSelected = normalizedValue.includes(optionValue);
                        return (
                            <MenuItem
                                key={service.code}
                                value={optionValue}
                                onClick={(e) => handleMenuItemClick(service.code, e)}
                                selected={isSelected}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                                        {service.code}
                                    </Typography>
                                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                        {service.definition}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        );
                    })
                ) : (
                    <MenuItem disabled>
                        <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                            No service codes found
                        </Typography>
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
};

export default SDPServiceCodesDropdownArray;
